import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Plus, Search, Video, FileText, Image, Download,
  Eye, Trash2, Play, File, MoreHorizontal, Upload, FolderOpen,
  Loader2, CloudUpload, X, ChevronDown, ChevronRight,
  BookOpen, Monitor, GraduationCap, ExternalLink, Clock, Star, CreditCard,
} from "lucide-react";
import LearningPaths from "@/components/LearningPaths";
import TutorialsTab from "@/components/TutorialsTab";
import CallScriptsTab from "@/components/CallScriptsTab";
import BusinessCardGenerator from "@/components/BusinessCardGenerator";
import { projectResources, matchesSearch as _matchesSearch } from "@/lib/resourcesProjection";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const CATEGORIES = [
  { id: "product-demo", label: "Product Demo" },
  { id: "brochure", label: "Brochure" },
  { id: "case-study", label: "Case Study" },
  { id: "template", label: "Template" },
  { id: "commercial", label: "Commercial" },
];

const PRODUCTS = [
  "AI-powered EHR", "Practice Management", "Medical Billing", "RevQ",
  "CCM/APCM", "RPM", "REACH", "AWV", "MIPS Advisory"
];

/* ─── Video Category Config ─── */
const VIDEO_CATEGORIES = [
  { id: "EHR/Practice Management", label: "EHR / Practice Mgmt", icon: Monitor, color: "bg-blue-500" },
  { id: "CCM", label: "CCM", icon: FileText, color: "bg-emerald-500" },
  { id: "APCM", label: "APCM", icon: FileText, color: "bg-teal-500" },
  { id: "RPM", label: "RPM", icon: FileText, color: "bg-violet-500" },
  { id: "Annual Wellness", label: "Annual Wellness", icon: FileText, color: "bg-amber-500" },
  { id: "Branded App", label: "Branded App", icon: Monitor, color: "bg-indigo-500" },
  { id: "General Tutorials", label: "General Tutorials", icon: GraduationCap, color: "bg-rose-500" },
];

/* ─── YouTube Embed Helper ─── */
function getYouTubeId(url) {
  const m = url?.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

/* ─── Video Card ─── */
function VideoCard({ asset, onClick }) {
  const videoId = asset.video_id || getYouTubeId(asset.url);
  const thumb = asset.thumbnail_url || (videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null);
  return (
    <button
      onClick={() => onClick(asset)}
      className="group text-left rounded-xl overflow-hidden bg-white hover:shadow-lg transition-all duration-200"
      style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
      data-testid={`video-card-${asset.id}`}
    >
      <div className="relative aspect-video bg-slate-100">
        {thumb ? (
          <img src={thumb} alt={asset.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Video className="h-8 w-8 text-slate-300" /></div>
        )}
        {/* Duration badge */}
        <div className="absolute bottom-1.5 right-1.5 bg-black/75 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded flex items-center gap-0.5">
          <Clock className="h-2.5 w-2.5" />Video
        </div>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-all flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-all scale-75 group-hover:scale-100 w-10 h-10 rounded-full bg-white/95 flex items-center justify-center shadow-xl">
            <Play className="h-4 w-4 text-[#1B365D] ml-0.5" />
          </div>
        </div>
      </div>
      <div className="p-3">
        <p className="text-sm font-medium text-[#1B365D] line-clamp-2 leading-snug">{asset.name}</p>
        {asset.subcategory && (
          <p className="text-[11px] text-slate-400 mt-1">{asset.subcategory}</p>
        )}
      </div>
    </button>
  );
}

/* ─── Video Player Modal ─── */
function VideoPlayerModal({ asset, open, onClose }) {
  if (!asset) return null;
  const videoId = asset.video_id || getYouTubeId(asset.url);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden" data-testid="video-player-modal">
        <DialogHeader className="sr-only"><DialogTitle>{asset.name}</DialogTitle></DialogHeader>
        <div className="aspect-video w-full bg-black">
          {videoId ? (
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={asset.name}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-white">Video unavailable</div>
          )}
        </div>
        <div className="p-4 border-t border-slate-100">
          <h3 className="font-semibold text-[#1B365D] text-base">{asset.name}</h3>
          {asset.description && <p className="text-sm text-slate-500 mt-1">{asset.description}</p>}
          <div className="flex items-center gap-2 mt-2">
            {asset.category && asset.category !== "tutorial" && (
              <Badge variant="outline" className="text-xs">{asset.category}</Badge>
            )}
            {asset.main_category && <Badge variant="outline" className="text-xs">{asset.main_category}</Badge>}
            {asset.subcategory && <Badge className="text-xs bg-[#0071E3]">{asset.subcategory}</Badge>}
            <a href={asset.url} target="_blank" rel="noopener noreferrer"
              className="ml-auto text-xs text-[#0071E3] hover:underline flex items-center gap-1">
              Open on YouTube <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Collapsible Section ─── */
function CollapsibleSection({ title, count, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-6">
      <button onClick={() => setOpen(v => !v)} className="flex items-center gap-2 w-full text-left mb-3 group" data-testid={`section-${title.replace(/\s+/g, '-').toLowerCase()}`}>
        {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        <h3 className="font-semibold text-[#1B365D] text-sm">{title}</h3>
        <Badge variant="outline" className="text-[10px] ml-1">{count}</Badge>
      </button>
      {open && children}
    </div>
  );
}

/* ─── Main Component ─── */
export default function DigitalAssets() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Deep-link support: /resources?tab=learning&course=ccm&chapter=ccm-01
  // When present, open the Learning Paths tab pre-selected at that chapter.
  const initialTab = searchParams.get("tab") || "videos";
  const deepLinkCourseId = searchParams.get("course") || null;
  const deepLinkChapterId = searchParams.get("chapter") || null;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUploadZone, setShowUploadZone] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeCat, setActiveCat] = useState("all");
  const [videoCatFilter, setVideoCatFilter] = useState(null);
  const fileInputRef = useRef(null);
  const [newAsset, setNewAsset] = useState({
    name: "", type: "document", category: "brochure",
    url: "", thumbnail_url: "", description: "", tags: [], product_related: []
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchAssets defined below; run once on mount
  useEffect(() => { fetchAssets(); }, []);

  const fetchAssets = async () => {
    try {
      const res = await axios.get(`${API}/assets`, { headers: { Authorization: `Bearer ${token}` } });
      setAssets(res.data);
    } catch { toast.error("Failed to load assets"); }
    finally { setLoading(false); }
  };

  const handleBulkUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      Array.from(files).forEach(f => formData.append("files", f));
      await axios.post(`${API}/assets/upload-batch`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => { if (p.total) setUploadProgress(Math.round((p.loaded / p.total) * 100)); },
      });
      toast.success(`${files.length} file${files.length > 1 ? "s" : ""} uploaded!`);
      setShowUploadZone(false);
      fetchAssets();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); setUploadProgress(0); if (fileInputRef.current) fileInputRef.current.value = ""; }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchAssets defined above is stable; only recreate callback on token change
  }, [token]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleBulkUpload(e.dataTransfer.files);
  }, [handleBulkUpload]);

  const handleCreateAsset = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/assets`, newAsset, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Asset added");
      setShowCreateDialog(false);
      setNewAsset({ name: "", type: "document", category: "brochure", url: "", thumbnail_url: "", description: "", tags: [], product_related: [] });
      fetchAssets();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add asset"); }
  };

  const handleDeleteAsset = async (id) => {
    if (!confirm("Delete this asset?")) return;
    try { await axios.delete(`${API}/assets/${id}`, { headers: { Authorization: `Bearer ${token}` } }); }
    catch { /* ok */ }
    setAssets(prev => prev.filter(a => a.id !== id));
    toast.success("Asset deleted");
  };

  /* ─── Derived Data ─── */
  const q = search.toLowerCase();
  const matchesSearch = (a) => _matchesSearch(a, q);

  // Type-based projection — see /app/frontend/src/lib/resourcesProjection.js
  // for the contract and why the legacy `id.startsWith("yt-")` filter was a bug.
  const { videos: resourceVideos, tutorials: tutorialVideos, docs: docAssets } = projectResources(assets, q);

  const groupedResourceVideos = VIDEO_CATEGORIES.map(cat => ({
    ...cat,
    videos: resourceVideos.filter(a => a.category === cat.id),
  })).filter(g => g.videos.length > 0);

  // Filtered groups for pill-filter UI
  const filteredVideoGroups = videoCatFilter
    ? groupedResourceVideos.filter(g => g.id === videoCatFilter)
    : groupedResourceVideos;

  // Tutorial groups (by main_category / subcategory)
  const tutorialGroups = {};
  tutorialVideos.forEach(v => {
    const mc = v.main_category || "Other";
    if (!tutorialGroups[mc]) tutorialGroups[mc] = {};
    const sc = v.subcategory || "General";
    if (!tutorialGroups[mc][sc]) tutorialGroups[mc][sc] = [];
    tutorialGroups[mc][sc].push(v);
  });

  const filteredDocs = docAssets.filter(a => activeCat === "all" || a.type === activeCat);

  const getTypeIcon = (type) => {
    if (type === "video") return <Video className="h-5 w-5 text-red-500" />;
    if (type === "document") return <FileText className="h-5 w-5 text-blue-500" />;
    if (type === "image") return <Image className="h-5 w-5 text-emerald-500" />;
    return <File className="h-5 w-5 text-slate-500" />;
  };

  const totalVideos = resourceVideos.length;
  const totalTutorials = tutorialVideos.length;
  const totalDocs = docAssets.length;

  return (
    <div data-testid="digital-assets-page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Resources</h1>
          <p className="page-subtitle">{assets.length} assets in your library</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search assets..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-10 w-64 rounded-xl border-slate-200" data-testid="search-assets" />
          </div>
          <Button variant="outline" className="rounded-xl" onClick={() => setShowUploadZone(v => !v)} data-testid="toggle-upload-zone-btn">
            <CloudUpload className="h-4 w-4 mr-2" /> Upload Files
          </Button>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="rounded-xl bg-[#0071e3] hover:bg-[#005bb5]" data-testid="add-asset-btn">
                <Plus className="h-4 w-4 mr-2" /> Add by URL
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="text-xl font-semibold text-[#1b365D]">Add New Asset</DialogTitle></DialogHeader>
              <form onSubmit={handleCreateAsset} className="space-y-4 mt-4">
                <div className="space-y-2"><Label>Asset Name *</Label><Input value={newAsset.name} onChange={e => setNewAsset({...newAsset, name: e.target.value})} required placeholder="e.g., EHR Product Overview" className="rounded-lg" data-testid="input-asset-name" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Type</Label>
                    <Select value={newAsset.type} onValueChange={v => setNewAsset({...newAsset, type: v})}>
                      <SelectTrigger className="rounded-lg" data-testid="select-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="video"><div className="flex items-center gap-2"><Video className="h-4 w-4" />Video</div></SelectItem>
                        <SelectItem value="document"><div className="flex items-center gap-2"><FileText className="h-4 w-4" />Document</div></SelectItem>
                        <SelectItem value="image"><div className="flex items-center gap-2"><Image className="h-4 w-4" />Image</div></SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Category</Label>
                    <Select value={newAsset.category} onValueChange={v => setNewAsset({...newAsset, category: v})}>
                      <SelectTrigger className="rounded-lg" data-testid="select-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2"><Label>URL *</Label><Input value={newAsset.url} onChange={e => setNewAsset({...newAsset, url: e.target.value})} required placeholder="https://..." className="rounded-lg" data-testid="input-url" /></div>
                <div className="space-y-2"><Label>Thumbnail URL</Label><Input value={newAsset.thumbnail_url} onChange={e => setNewAsset({...newAsset, thumbnail_url: e.target.value})} placeholder="https://..." className="rounded-lg" data-testid="input-thumbnail" /></div>
                <div className="space-y-2"><Label>Related Products</Label>
                  <div className="flex flex-wrap gap-2">{PRODUCTS.map(p => (
                    <Badge key={p} variant={newAsset.product_related.includes(p) ? "default" : "outline"}
                      className={`cursor-pointer transition-colors ${newAsset.product_related.includes(p) ? "bg-[#0071e3] hover:bg-[#005bb5]" : "hover:bg-slate-100"}`}
                      onClick={() => { const prods = newAsset.product_related.includes(p) ? newAsset.product_related.filter(x => x !== p) : [...newAsset.product_related, p]; setNewAsset({...newAsset, product_related: prods}); }}>
                      {p}
                    </Badge>
                  ))}</div>
                </div>
                <div className="space-y-2"><Label>Description</Label><Textarea value={newAsset.description} onChange={e => setNewAsset({...newAsset, description: e.target.value})} className="rounded-lg" rows={3} data-testid="input-description" /></div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} className="rounded-lg">Cancel</Button>
                  <Button type="submit" className="rounded-lg bg-[#1b365D] hover:bg-[#142846]" data-testid="submit-asset-btn">Add Asset</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {/* Upload Zone */}
      {showUploadZone && (
        <div className="page-content pb-0">
          <div className={`relative border-2 border-dashed rounded-2xl p-8 transition-all duration-200 ${dragOver ? "border-[#0071e3] bg-blue-50/60" : "border-slate-300 bg-slate-50/50 hover:border-slate-400"}`}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop} data-testid="upload-drop-zone">
            <button onClick={() => setShowUploadZone(false)} className="absolute top-3 right-3 p-1 rounded-lg hover:bg-slate-200 transition-colors"><X className="h-4 w-4 text-slate-400" /></button>
            <input ref={fileInputRef} type="file" multiple accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" onChange={e => handleBulkUpload(e.target.files)} className="hidden" data-testid="bulk-file-input" />
            <div className="flex flex-col items-center gap-3">
              {uploading ? (
                <>
                  <Loader2 className="h-10 w-10 text-[#0071e3] animate-spin" />
                  <p className="text-sm font-semibold text-[#1B365D]">Uploading... {uploadProgress}%</p>
                  <div className="w-64 h-2 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-[#0071e3] transition-all duration-300 rounded-full" style={{ width: `${uploadProgress}%` }} /></div>
                </>
              ) : (
                <>
                  <CloudUpload className="h-10 w-10 text-slate-400" />
                  <div className="text-center">
                    <p className="text-sm font-semibold text-[#1B365D]">Drag & drop files here</p>
                    <p className="text-xs text-slate-500 mt-1">Supports images, videos, PDFs, and documents</p>
                  </div>
                  <Button variant="outline" className="rounded-xl mt-1" onClick={() => fileInputRef.current?.click()} data-testid="browse-files-btn">
                    <Upload className="h-4 w-4 mr-2" /> Browse Files
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="page-content">
        <Tabs value={activeTab} onValueChange={(v) => {
          setActiveTab(v);
          // Keep the URL in sync so a browser refresh preserves the tab,
          // and so the Sales Dialer deep-link stays valid during navigation.
          const next = new URLSearchParams(searchParams);
          next.set("tab", v);
          if (v !== "learning") { next.delete("course"); next.delete("chapter"); }
          setSearchParams(next, { replace: true });
        }}>
          <TabsList className="mb-6 bg-slate-100 p-1 rounded-xl">
            <TabsTrigger value="videos" className="rounded-lg data-[state=active]:bg-white" data-testid="tab-videos">
              <Video className="h-4 w-4 mr-2" /> Videos <Badge variant="secondary" className="ml-2 text-[10px] bg-slate-200">{totalVideos}</Badge>
            </TabsTrigger>
            <TabsTrigger value="tutorials" className="rounded-lg data-[state=active]:bg-white" data-testid="tab-tutorials">
              <GraduationCap className="h-4 w-4 mr-2" /> Tutorials <Badge variant="secondary" className="ml-2 text-[10px] bg-slate-200">{totalTutorials}</Badge>
            </TabsTrigger>
            <TabsTrigger value="assets" className="rounded-lg data-[state=active]:bg-white" data-testid="tab-assets">
              <FolderOpen className="h-4 w-4 mr-2" /> Documents & Files <Badge variant="secondary" className="ml-2 text-[10px] bg-slate-200">{totalDocs}</Badge>
            </TabsTrigger>
            <TabsTrigger value="learning" className="rounded-lg data-[state=active]:bg-white" data-testid="tab-learning">
              <BookOpen className="h-4 w-4 mr-2" /> Learning Paths
            </TabsTrigger>
            <TabsTrigger value="scripts" className="rounded-lg data-[state=active]:bg-white" data-testid="tab-scripts">
              <FileText className="h-4 w-4 mr-2" /> Call Scripts
            </TabsTrigger>
            <TabsTrigger value="bizcard" className="rounded-lg data-[state=active]:bg-white" data-testid="tab-bizcard">
              <CreditCard className="h-4 w-4 mr-2" /> Business Card
            </TabsTrigger>
          </TabsList>

          {/* ════ VIDEOS TAB ════ */}
          <TabsContent value="videos">
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => <div key={i} className="skeleton h-48 rounded-xl" />)}
              </div>
            ) : (
              <div>
                {/* YouTube Channel Strip */}
                <div className="mb-4 px-4 py-3 rounded-xl flex items-center justify-between" style={{ background: "linear-gradient(135deg, #282828 0%, #181818 50%, #1a1a1a 100%)" }}>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <div className="w-7 h-5 rounded-[3px] flex items-center justify-center" style={{ background: "#FF0000" }}>
                        <Play className="h-3 w-3 text-white fill-white ml-px" />
                      </div>
                      <span className="text-white text-sm font-bold tracking-tight">Enable Healthcare</span>
                    </div>
                    <span className="text-[11px] text-white/50 hidden sm:inline">75+ training videos</span>
                  </div>
                  <a
                    href="https://www.youtube.com/@enablehealthcare1/videos"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-1.5 rounded-lg text-white text-xs font-semibold hover:brightness-110 transition-all"
                    style={{ background: "#CC0000" }}
                    data-testid="youtube-channel-link"
                  >
                    View Channel
                  </a>
                </div>

                {/* Category Pill Filters */}
                <div className="flex gap-1.5 mb-5 flex-wrap">
                  {[{ id: "all", label: "All", icon: Video }].concat(VIDEO_CATEGORIES).map((cat) => {
                    const isActive = (cat.id === "all" && !videoCatFilter) || videoCatFilter === cat.id;
                    const count = cat.id === "all"
                      ? resourceVideos.filter(matchesSearch).length
                      : resourceVideos.filter(a => a.category === cat.id && matchesSearch(a)).length;
                    if (cat.id !== "all" && count === 0) return null;
                    const Icon = cat.icon || Video;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => setVideoCatFilter(cat.id === "all" ? null : cat.id)}
                        className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 flex items-center gap-1.5 ${
                          isActive
                            ? "bg-[#1B365D] text-white shadow-sm"
                            : "bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                        style={!isActive ? { boxShadow: "0 1px 3px rgba(0,0,0,0.06)" } : {}}
                        data-testid={`video-filter-${cat.id}`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {cat.label}
                        <Badge variant="secondary" className={`text-[9px] px-1.5 ${isActive ? "bg-white/20 text-white" : "bg-slate-100"}`}>{count}</Badge>
                      </button>
                    );
                  })}
                </div>

                {/* Grouped Video Sections */}
                {filteredVideoGroups.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Video className="h-8 w-8 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm">No videos found</p>
                  </div>
                ) : (
                  filteredVideoGroups.map(group => (
                    <div key={group.id} className="mb-6" data-testid={`video-section-${group.id}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`w-6 h-6 rounded-md ${group.color} flex items-center justify-center`}>
                          <group.icon className="h-3 w-3 text-white" />
                        </div>
                        <h3 className="font-semibold text-[#1B365D] text-sm">{group.label}</h3>
                        <span className="text-[10px] text-slate-400">{group.videos.length} video{group.videos.length !== 1 ? "s" : ""}</span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                        {group.videos.map(v => <VideoCard key={v.id} asset={v} onClick={setSelectedVideo} />)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </TabsContent>

          {/* ════ TUTORIALS TAB ════ */}
          <TabsContent value="tutorials">
            <TutorialsTab />
          </TabsContent>

          {/* ════ DOCUMENTS & FILES TAB ════ */}
          <TabsContent value="assets">
            <div className="flex gap-2 mb-4 flex-wrap">
              {[{ id: "all", label: "All" }, { id: "document", label: "Documents" }, { id: "image", label: "Images" }].map(t => (
                <Button key={t.id} variant={activeCat === t.id ? "default" : "outline"} size="sm" className={`rounded-lg text-xs ${activeCat === t.id ? "bg-[#1B365D]" : ""}`}
                  onClick={() => setActiveCat(t.id)}>{t.label}</Button>
              ))}
            </div>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => <div key={i} className="skeleton h-64 rounded-xl" />)}
              </div>
            ) : filteredDocs.length === 0 ? (
              <div className="empty-state"><div className="empty-state-icon"><FolderOpen className="h-10 w-10" /></div><h3 className="empty-state-title">No assets found</h3><p className="empty-state-text">Add assets to start building your library.</p></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredDocs.map((asset, index) => (
                  <Card key={asset.id} className={`card-hover overflow-hidden animate-fadeIn stagger-${(index % 5) + 1}`} data-testid={`asset-card-${asset.id}`}>
                    <div className="relative h-40 bg-slate-100">
                      {asset.thumbnail_url ? (
                        <img src={asset.thumbnail_url} alt={asset.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">{getTypeIcon(asset.type)}</div>
                      )}
                      {asset.type === "video" && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center"><Play className="h-5 w-5 text-[#1b365D] ml-1" /></div>
                        </div>
                      )}
                      {asset.category && (
                        <div className="absolute top-3 left-3">
                          <Badge className="bg-white/90 text-slate-700 hover:bg-white/90 text-xs">{CATEGORIES.find(c => c.id === asset.category)?.label || asset.category}</Badge>
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {getTypeIcon(asset.type)}
                          <h3 className="font-semibold text-[#1b365D] text-sm line-clamp-1">{asset.name}</h3>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 -mr-2"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem><Eye className="h-4 w-4 mr-2" />Preview</DropdownMenuItem>
                            <DropdownMenuItem><Download className="h-4 w-4 mr-2" />Download</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDeleteAsset(asset.id)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      {asset.description && <p className="text-xs text-slate-500 line-clamp-2 mb-3">{asset.description}</p>}
                      <div className="flex items-center justify-between text-xs text-slate-400">
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{asset.views || 0}</span>
                          <span className="flex items-center gap-1"><Download className="h-3 w-3" />{asset.downloads || 0}</span>
                        </div>
                        {asset.product_related?.length > 0 && <Badge variant="outline" className="text-xs">{asset.product_related[0]}</Badge>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ════ LEARNING PATHS TAB ════ */}
          <TabsContent value="learning">
            <LearningPaths initialCourseId={deepLinkCourseId} initialChapterId={deepLinkChapterId} />
          </TabsContent>

          {/* ════ CALL SCRIPTS TAB (read-only mirror of Sales Dialer scripts) ════ */}
          <TabsContent value="scripts">
            <CallScriptsTab />
          </TabsContent>

          {/* ════ BUSINESS CARD TAB ════ */}
          <TabsContent value="bizcard">
            <BusinessCardGenerator />
          </TabsContent>
        </Tabs>
      </div>

      {/* Video Player Modal */}
      <VideoPlayerModal asset={selectedVideo} open={!!selectedVideo} onClose={() => setSelectedVideo(null)} />
    </div>
  );
}
